import React, { useState } from 'react';
import { supabase } from '../supabase';
import { startCheckout } from '../lib/billing';

// ─── WelcomeModal ───────────────────────────────────────────────────────────
//
// One-time post-signup nudge to start the trial. Shown when:
//   - operator just signed up (companies.has_seen_welcome === false), AND
//   - they don't have an active subscription yet.
//
// Two paths:
//   "Start trial" → fires create-checkout-session and redirects. We don't
//     stamp has_seen_welcome here because the Stripe redirect navigates
//     away anyway; the resulting subscription state will keep the
//     paywall/welcome from re-firing on return.
//   "Skip for now" → fire-and-forget UPDATE has_seen_welcome=true, close
//     the modal. Operator continues into Posted; the paywall will hit
//     when they try to do anything that's not /settings.
//
// The 30-day-from-now date is computed client-side for the copy. Not
// authoritative — Stripe sets the actual trial end on subscription
// creation — but accurate enough for "no charge until ~ May 31".

function thirtyDaysFromNow() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function WelcomeModal({ companyId, onSkip }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleStart() {
    if (busy) return;
    setBusy(true);
    setError('');
    const ok = await startCheckout('monthly', {
      onError: (r) => {
        const msg = (r && r.body && r.body.message) || 'Couldn\'t start checkout. Try again.';
        setError(msg);
      },
    });
    if (!ok) setBusy(false);
    // On success the page navigates away, no need to clear state.
  }

  async function handleSkip() {
    if (busy) return;
    setBusy(true);
    try {
      // Fire-and-forget — we don't want to block the operator on a
      // network round-trip just to dismiss a modal. If the UPDATE fails
      // they'll see the modal again next page-load; benign.
      if (companyId) {
        supabase
          .from('companies')
          .update({ has_seen_welcome: true })
          .eq('id', companyId)
          .then(() => {});
      }
    } finally {
      setBusy(false);
      onSkip?.();
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="no-print"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 17, 22, 0.55)',
        zIndex: 80,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          background: '#FFF',
          border: '1px solid #E5E5E5',
          borderRadius: 16,
          width: '100%',
          maxWidth: 460,
          padding: '32px 28px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div className="text-center" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: '#111', marginBottom: 6 }}>
            Welcome to Posted!
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
            Start your 30-day free trial — no charge until <span style={{ fontWeight: 600, color: '#111' }}>{thirtyDaysFromNow()}</span>.
            <br />
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>Card required upfront. Cancel anytime from Settings.</span>
          </div>
        </div>

        {error && (
          <div style={{
            marginBottom: 16, fontSize: 12, color: '#B91C1C',
            background: '#FEE2E2', borderRadius: 8, padding: '8px 12px',
          }}>
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleStart}
          disabled={busy}
          className="w-full"
          style={{
            background: '#2563EB',
            color: '#FFF',
            borderRadius: 10,
            border: 'none',
            padding: '12px 20px',
            fontSize: 15,
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
            marginBottom: 10,
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Starting…' : 'Start trial'}
        </button>
        <button
          type="button"
          onClick={handleSkip}
          disabled={busy}
          className="w-full"
          style={{
            background: 'transparent',
            color: '#6B7280',
            borderRadius: 10,
            border: 'none',
            padding: '10px 20px',
            fontSize: 13,
            fontWeight: 500,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
